// findingSectionMap — Decision Surface plan, Stage 2 (§3.3 + §4.2).
//
// The single authored coupling between a finding and the document
// section(s) it affects. Derived from the section's ``target_content``
// tokens (NOT a hand-maintained parallel section table): a finding kind
// maps to one or more ``target_content`` tokens, and a Coverage Map node
// is "affected" when its ``target_content`` intersects those tokens.
//
// The map is typed ``Record<AnyFindingKind, …>`` so the compiler fails
// the build if a new finding kind is added to any of the three enums
// without a mapping here (the plan's "enum completeness" guard); a
// runtime test mirrors the universe as belt-and-suspenders.

import type { CoverageSectionRow } from '@/types/documentStructure'
import type { ControlsCoverageFindingKind } from '@/types/controlsCoverageFindings'
import type { DataDictionaryFindingKind } from '@/types/dataDictionaryFindings'
import type { ThreatModelFindingKind } from '@/types/threatModelFindings'

/** Every finding kind across the three G3.7 findings enums. */
export type AnyFindingKind =
  | DataDictionaryFindingKind
  | ThreatModelFindingKind
  | ControlsCoverageFindingKind

export type FindingSeverity = 'critical' | 'high' | 'medium'

/**
 * finding_kind → ``target_content`` tokens it lands on (plan §3.3).
 *
 *  - threat-model → ``threat_model`` (§9c) + ``asvs_mapping`` (§9d)
 *  - data-dictionary → ``data_classification_matrix`` (§9a) + the
 *    data-dictionary appendix
 *  - controls-coverage → ``control_matrix`` (§9e) + ``compliance_risks`` (§9)
 *
 * Typed as a total ``Record`` over the union → adding a kind to any enum
 * without a row here is a compile error.
 */
export const FINDING_KIND_TO_TOKENS: Record<AnyFindingKind, readonly string[]> = {
  // threat-model
  missing_mandatory_asset: ['threat_model', 'asvs_mapping'],
  missing_stride_category: ['threat_model', 'asvs_mapping'],
  no_asset_sections: ['threat_model', 'asvs_mapping'],
  // data-dictionary
  missing_mandatory_table: ['data_classification_matrix', 'data_dictionary_appendix'],
  missing_mandatory_column: ['data_classification_matrix', 'data_dictionary_appendix'],
  retention_tbd: ['data_classification_matrix', 'data_dictionary_appendix'],
  retention_below_floor: ['data_classification_matrix', 'data_dictionary_appendix'],
  // controls-coverage
  missing_in_scope_control: ['control_matrix', 'compliance_risks'],
  no_hazard_tables: ['control_matrix', 'compliance_risks'],
}

/** All known finding kinds (runtime mirror of the three unions). */
export const ALL_FINDING_KINDS = Object.keys(
  FINDING_KIND_TO_TOKENS,
) as AnyFindingKind[]

export function tokensForFindingKind(kind: AnyFindingKind): readonly string[] {
  return FINDING_KIND_TO_TOKENS[kind] ?? []
}

/**
 * Coverage Map section ids whose ``target_content`` intersects the
 * finding's tokens — i.e. the node(s) to highlight when this finding is
 * focused. Empty when the resolved template has no matching section
 * (e.g. a non-compliance template with no §9 family).
 */
export function sectionIdsForFinding(
  kind: AnyFindingKind,
  sections: readonly CoverageSectionRow[],
): string[] {
  const tokens = new Set(tokensForFindingKind(kind))
  if (tokens.size === 0) return []
  return sections
    .filter((s) => (s.target_content ?? []).some((t) => tokens.has(t)))
    .map((s) => s.id)
}

/**
 * Reverse lookup: the finding kinds that target a given section (for the
 * node-hover "findings feeding this section" affordance).
 */
export function findingKindsForSection(
  section: CoverageSectionRow,
): AnyFindingKind[] {
  const toks = new Set(section.target_content ?? [])
  if (toks.size === 0) return []
  return ALL_FINDING_KINDS.filter((k) =>
    FINDING_KIND_TO_TOKENS[k].some((t) => toks.has(t)),
  )
}

// --------------------------------------------------------------------- //
// Blocking class (plan §4.2 / Stage 2) — computed, never stored.
// --------------------------------------------------------------------- //
export type BlockingClass = 'blocks' | 'deferrable-as-TBD' | 'optional'

export interface BlockingClassInputs {
  severity: FindingSeverity
  /** A required (non-optional) section is in the finding's blast radius. */
  affectsRequiredSection: boolean
  /** The gate this finding sits behind is optional at the current
   *  intervention budget (e.g. skipped at Minimal). */
  gateOptional?: boolean
  /** An accepted waiver already covers this finding. */
  waived?: boolean
}

/**
 * Derive how a finding constrains the export, in the architect's terms:
 *
 *  - ``blocks`` — the pre-export gate will REFUSE export until resolved.
 *  - ``deferrable-as-TBD`` — the document still exports; the affected
 *    required section prints a ``TBD`` placeholder.
 *  - ``optional`` — no export impact (optional section, skipped gate, or
 *    an accepted waiver).
 *
 * Severity is used as the PEG-enforcement proxy: the enforcing PEG
 * invariants block at ``block_at_severity = HIGH`` (see
 * ``pre_export_gate``), so ``critical|high`` on a required section under
 * an enforcing gate genuinely blocks, while ``medium`` is advisory and
 * degrades to a TBD. (A future refinement could plumb the exact
 * per-invariant PEG mode from the API instead of inferring from
 * severity; documented in the plan as the ideal input.)
 */
export function blockingClassFor(i: BlockingClassInputs): BlockingClass {
  if (i.waived) return 'optional'
  if (i.gateOptional) return 'optional'
  if (!i.affectsRequiredSection) return 'optional'
  if (i.severity === 'critical' || i.severity === 'high') return 'blocks'
  return 'deferrable-as-TBD'
}

// --------------------------------------------------------------------- //
// Per-section findings rollup — drives the Coverage Map node annotation
// (Stage 2 UI). Pure + fetch-agnostic so it's unit-testable.
// --------------------------------------------------------------------- //

/** The minimal finding shape the rollup needs (any of the three G3.7
 *  findings rows narrows to this). */
export interface NormalizedFinding {
  finding_kind: AnyFindingKind
  severity: FindingSeverity
  waived?: boolean
}

export interface NodeFindingSummary {
  /** Worst blocking class among the findings landing on this section. */
  worst: BlockingClass
  /** # findings that block export on this section. */
  blocks: number
  /** # findings that would print TBD here (non-blocking). */
  tbd: number
  /** Total findings landing on this section (excludes waived/optional). */
  total: number
}

const _CLASS_RANK: Record<BlockingClass, number> = {
  blocks: 2,
  'deferrable-as-TBD': 1,
  optional: 0,
}

/**
 * Roll findings up by the section they land on, so each Coverage Map
 * node can show "⛔ N block export" / "⚠ N print TBD". A finding that
 * targets several sections counts on each. Waived / optional-impact
 * findings contribute to neither counter (they don't constrain export).
 */
export function summarizeFindingsBySection(
  sections: readonly CoverageSectionRow[],
  findings: readonly NormalizedFinding[],
  opts: { gateOptional?: boolean } = {},
): Map<string, NodeFindingSummary> {
  const byId = new Map<string, CoverageSectionRow>(sections.map((s) => [s.id, s]))
  const out = new Map<string, NodeFindingSummary>()
  for (const f of findings) {
    for (const sectionId of sectionIdsForFinding(f.finding_kind, sections)) {
      const section = byId.get(sectionId)
      if (!section) continue
      const cls = blockingClassFor({
        severity: f.severity,
        affectsRequiredSection: section.required,
        gateOptional: opts.gateOptional,
        waived: f.waived,
      })
      if (cls === 'optional') continue
      const cur =
        out.get(sectionId) ?? { worst: 'optional', blocks: 0, tbd: 0, total: 0 }
      if (cls === 'blocks') cur.blocks += 1
      else cur.tbd += 1
      cur.total += 1
      if (_CLASS_RANK[cls] > _CLASS_RANK[cur.worst]) cur.worst = cls
      out.set(sectionId, cur)
    }
  }
  return out
}
